import React, { useEffect, useState } from "react"
import LogoutButton from "../Authentication/LogoutButton"
import { useAuth0 } from "@auth0/auth0-react"
import { useNavigate } from "react-router-dom"
import { IoIosWarning } from "react-icons/io"
import { getDateFormatted, getFirstOpenDateToday } from "../../utils/generalUtils"
import useFetchSettings from "../../hooks/useFetchSettings"
import useLogging from "../../hooks/useLogging"
import { useQueryClient } from "@tanstack/react-query"
import { usePermissionsList, useUserRole } from "../../hooks/queries/usePermissions"

const PersonalSettings = ({ handleSubmit, nurses }) => {
	const queryClient = useQueryClient()
	// Safely derive the nurse table data. If nurses is null, use an empty array.
	const nurseTableData = nurses ? (Array.isArray(nurses) ? nurses : Object.values(nurses)) : []
	const { getAccessTokenSilently, user, isAuthenticated } = useAuth0()
	const [role, setRole] = useState("")
	const permissions = usePermissionsList()
	const roles = useUserRole()
	const [accountSettings, setAccountSettings] = useState({
		firstName: "",
		lastName: "",
		email: "",
		profilePicture: ""
	})
	// Find the current nurse only if nurseTableData is not empty.
	const nurse = nurseTableData.find(
		nurse => nurse.nurseEmail?.toLowerCase() === user.email?.toLowerCase()
	)
	const navigate = useNavigate()
	const { settingsData } = useFetchSettings()
	const logging = useLogging()
	const fetchPermissions = async () => {
		try {
			await queryClient.invalidateQueries({ queryKey: ["permissions"] })
		} catch (error) {
			logging.error("Error refreshing permissions:", {
				error: error.message,
				stack: error.stack
			})
			if (!isAuthenticated) {
				navigate("/error")
			}
		}
	}

	const fetchUserRole = async () => {
		try {
			let totalRoles = ""

			const roleKeys = [
				"rol_WwU4j42n5ETRTNXl",
				"rol_mPO9bv3p6ha8EHar",
				"rol_5V3q47WJK8BuaenM",
				"rol_anI0OMNgygRNLElC",
				"rol_THis3DDVAA2hZtiq",
				"rol_c9WwyN9cfHrMm1ZF",
				"rol_ExObANc6mUp5nkFr",
				"rol_nMzJ8gSACSKZvhHl"
			]

			const roleNames = [
				"Oncare Admin",
				"Executive",
				"Executive Team",
				"Floor Nurses",
				"Medical Assistants",
				"Charge Nurse",
				"Provider",
				"Scheduler"
			]

			roleKeys.forEach((key, index) => {
				if (roles.includes(key)) {
					totalRoles += `${roleNames[index]}, `
				}
			})

			if (totalRoles === "") {
				totalRoles = "No Role"
			}

			// Remove trailing comma and space if present
			totalRoles = totalRoles.replace(/,\s*$/, "")

			setRole(totalRoles)
		} catch (error) {
			console.error("Error fetching user role:", error)
		}
	}

	useEffect(() => {
		// Assign role based on permissions
		fetchUserRole()
		// `fetchUserRole` is stable within this component context.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [permissions, roles])

	useEffect(() => {
		setAccountSettings({
			firstName: user.given_name || "",
			lastName: user.family_name || "",
			email: user.email || "",
			profilePicture: user.picture || "https://via.placeholder.com/150"
		})
	}, [getAccessTokenSilently, navigate, user])

	const handleAssumeChargeNurseRole = async () => {
		try {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
			const centerOpen = settingsData?.centerOpen
			const centerClosedDates = settingsData?.center_closed_dates || []
			const date = getDateFormatted(
				getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)
			)

			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/api/assume-charge-nurse-role?date=${date}`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json"
					}
				}
			)

			if (!response.ok) {
				throw new Error("Failed to assume Charge Nurse role")
			}

			const data = await response.json()

			if (data.success) {
				window.alert("You have successfully assumed the Charge Nurse role.")

				try {
					// Get fresh token with updated permissions
					await getAccessTokenSilently({
						audience: process.env.REACT_APP_API_IDENTIFIER,
						ignoreCache: true
					})

					// Wait briefly for Auth0 role propagation, then update permissions
					await new Promise(resolve => setTimeout(resolve, 1000))
					await fetchPermissions()
				} catch (error) {
					logging.error("Error refreshing permissions after role assumption:", error)
					window.alert(
						"Role assumed successfully, but there was an issue updating your permissions. Please refresh the page."
					)
				}
			} else {
				window.alert("Failed to assume Charge Nurse role.")
			}
		} catch (error) {
			logging.warn("Error assuming Charge Nurse role:", {
				error: error.message,
				stack: error.stack
			})
			window.alert("An error occurred while attempting to assume the Charge Nurse role.")
		}
	}

	return (
		<div className="mt-10">
			<div className="mt-6">
				<form onSubmit={handleSubmit}>
					<div className="grid grid-cols-1 gap-6">
						<div className="col-span-1 flex flex-col items-center">
							<img
								className="h-24 w-24 rounded-full"
								src={accountSettings.profilePicture}
								alt="Profile"
							/>
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
							<div className="col-span-1">
								<label
									htmlFor="firstName"
									className="block text-sm font-medium text-gray-700"
									style={{ fontFamily: "Manrope", color: "#4A4A4A" }}
								>
									First Name
								</label>
								<input
									type="text"
									name="firstName"
									id="firstName"
									value={accountSettings.firstName}
									readOnly
									className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
								/>
							</div>
							<div className="col-span-1">
								<label
									htmlFor="lastName"
									className="block text-sm font-medium text-gray-700"
									style={{ fontFamily: "Manrope", color: "#4A4A4A" }}
								>
									Last Name
								</label>
								<input
									type="text"
									name="lastName"
									id="lastName"
									value={accountSettings.lastName}
									readOnly
									className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
								/>
							</div>
							<div className="col-span-2">
								<label
									htmlFor="role"
									className="block text-sm font-medium text-gray-700"
									style={{ fontFamily: "Manrope", color: "#4A4A4A" }}
								>
									Role
								</label>
								<input
									type="text"
									name="role"
									id="role"
									value={role}
									readOnly
									className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
								/>
							</div>
							<div className="col-span-2">
								<label
									htmlFor="email"
									className="block text-sm font-medium text-gray-700"
									style={{ fontFamily: "Manrope", color: "#4A4A4A" }}
								>
									Email
								</label>
								<input
									type="text"
									name="email"
									id="email"
									value={accountSettings.email}
									readOnly
									className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
								/>
							</div>
						</div>
					</div>

					<div className="mt-8 flex justify-center space-x-10">
						{/* Only render the "Assume Charge Nurse Role" button if nurse data is available */}
						{nurses && nurse && nurse.eligibleChargeNurse && (
							<button
								type="button"
								className="flex items-center rounded-md border border-red-500 bg-red-100 px-3 py-2 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
								onClick={handleAssumeChargeNurseRole}
							>
								<IoIosWarning className="mr-2 text-lg text-red-700" />
								Assume Charge Nurse Role
							</button>
						)}
						{isAuthenticated && (
							<LogoutButton className="rounded-md bg-acuityHigh px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500" />
						)}
					</div>
				</form>
			</div>
		</div>
	)
}

export default PersonalSettings
